import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { TransporteApiService } from '@services/transporte-api.service';
import type { ClienteEmpresa, RegistroHistorial } from '@schemas/catalogo';
import type {
  Cliente,
  ClienteDetalle,
  DatosAltaRazonSocial,
  DatosBroker,
  DatosCliente,
  DatosEmpresaPagadora,
  DatosEsquemaPago,
  DatosFiscales,
  DatosGrupo,
  DatosParejaCondiciones,
  DatosRelacionComercial,
  DatosSeguimiento,
  DetalleRelacionComercial,
  ElementoCatalogo,
  EmpresaPagadora,
  EntidadClientes,
  EsquemaPago,
  Facturadora,
  GrupoCliente,
  ListaBrokersCliente,
  ListaClavesSat,
  ListaClientes,
  ListaCondicionesCliente,
  ListaRazonesSociales,
  ListaRazonesSocialesGlobal,
  ListaSeguimientoCliente,
  RazonSocialGlobal,
  RespuestaDatosFiscales,
  RespuestaRazonSocial,
} from '@schemas/clientes';

/**
 * Cliente del contrato v1 de clientesapi (`/api/v1/clientes/...`): grupos,
 * clientes (nombre comercial), razones sociales (con su alta completa y sus datos
 * fiscales), broker, seguimiento, condiciones comerciales de cada pareja razon
 * social → facturadora, esquemas de pago, empresas pagadoras, relaciones
 * comerciales (delgadas: la facturacion ya no va en ellas), historial y los
 * catalogos de apoyo. Es el UNICO lugar que conoce esas rutas.
 *
 * Toda mutacion lleva `motivo` y, si edita algo existente, la `version` leida
 * (en razones sociales, condiciones comerciales, broker y seguimiento es la DEL
 * CLIENTE). Un 409 (`CONFLICTO_VERSION`) significa que otro usuario cambio el
 * dato. Las pantallas deciden por el `codigo` del error, nunca por su mensaje.
 */
/**
 * El motivo de un alta (fnomina `9a55712`, «corte de revision» del 2026-10-19: las altas ya no piden motivo y en la
 * bitacora queda «Alta»). SINAY: clientesapi exige `motivo` en toda mutacion (`MOTIVO_REQUERIDO`), asi que las altas
 * que van a clientesapi lo mandan con este texto.
 */
export const MOTIVO_ALTA = 'Alta';

@Injectable({ providedIn: 'root' })
export class ClientesApiService {
  private readonly api = inject(TransporteApiService);
  private readonly base = '/api/v1/clientes';

  // ── Grupos ─────────────────────────────────────────────────────────────────

  grupos(): Observable<GrupoCliente[]> {
    return this.api.pedir<{ grupos: GrupoCliente[] }>('GET', `${this.base}/grupos`).pipe(map((d) => d.grupos));
  }

  altaGrupo(grupo: DatosGrupo, motivo: string): Observable<GrupoCliente> {
    return this.api.pedir('POST', `${this.base}/grupos`, { cuerpo: { grupo, motivo } });
  }

  actualizarGrupo(id: number, grupo: DatosGrupo, version: number, motivo: string): Observable<GrupoCliente> {
    return this.api.pedir('PUT', `${this.base}/grupos/${id}`, { cuerpo: { grupo, version, motivo } });
  }

  // ── Clientes (nombre comercial) ────────────────────────────────────────────

  clientes(): Observable<Cliente[]> {
    return this.api.pedir<ListaClientes>('GET', this.base).pipe(map((d) => [...d.clientes]));
  }

  /**
   * Los clientes a los que una facturadora les factura (algun tramo de la pareja vigente en `fecha`
   * o despues; hoy por omision). Si nomina no se pudo leer, la lista llega vacia con el aviso
   * `NOMINA_NO_DISPONIBLE`: por eso se entrega la respuesta completa.
   */
  clientesQueFactura(facturadoraId: number, fecha?: string | null): Observable<ListaClientes> {
    return this.api.pedir('GET', this.base, { consulta: { facturadora: facturadoraId, fecha } });
  }

  /** Con razones sociales, brokers, seguimiento y asignaciones (con avisos). */
  cliente(id: number): Observable<ClienteDetalle> {
    return this.api.pedir('GET', `${this.base}/${id}`);
  }

  altaCliente(cliente: DatosCliente, motivo: string): Observable<Cliente> {
    return this.api.pedir('POST', this.base, { cuerpo: { cliente, motivo } });
  }

  actualizarCliente(id: number, cliente: DatosCliente, version: number, motivo: string): Observable<Cliente> {
    return this.api.pedir('PUT', `${this.base}/${id}`, { cuerpo: { cliente, version, motivo } });
  }

  // ── Razones sociales ───────────────────────────────────────────────────────

  /**
   * La lista global: una fila por vinculo (el vigente y los cerrados) con su domicilio fiscal y su
   * regimen. Con `rfc` y sin `cliente` salen tambien las de ese RFC que no son de ningun cliente.
   */
  razonesSocialesGlobal(filtro: { cliente?: number; rfc?: string } = {}): Observable<RazonSocialGlobal[]> {
    return this.api
      .pedir<ListaRazonesSocialesGlobal>('GET', `${this.base}/razones-sociales`, { consulta: filtro })
      .pipe(map((d) => [...d.razones_sociales]));
  }

  /** Las del cliente (sus vinculos) con la version del cliente. */
  razonesSociales(clienteId: number): Observable<ListaRazonesSociales> {
    return this.api.pedir('GET', `${this.base}/${clienteId}/razones-sociales`);
  }

  /**
   * Alta completa por RFC (`resultado`: `CREADA`, `LIGADA` o `CREADA_DE_INTERNA`). Si el RFC no esta
   * en SINAY y faltan datos fiscales: 404 `RAZON_SOCIAL_INEXISTENTE` o 400 `CAMPO_FALTANTE`.
   * `version` es la del cliente (opcional en el alta).
   */
  altaRazonSocial(
    clienteId: number,
    razon: DatosAltaRazonSocial,
    version: number | null,
    motivo: string,
  ): Observable<RespuestaRazonSocial> {
    return this.api.pedir('POST', `${this.base}/${clienteId}/razones-sociales`, {
      cuerpo: { razon_social: razon, ...(version === null ? {} : { version }), motivo },
    });
  }

  /** `vigenteHasta` cierra el vinculo; `null` lo reabre. `version` es la del cliente. */
  cambiarVigenciaRazonSocial(
    clienteId: number,
    vinculoId: number,
    vigenteHasta: string | null,
    version: number,
    motivo: string,
  ): Observable<RespuestaRazonSocial> {
    return this.api.pedir('PUT', `${this.base}/${clienteId}/razones-sociales/${vinculoId}`, {
      cuerpo: { razon_social: { vigente_hasta: vigenteHasta }, version, motivo },
    });
  }

  /**
   * Los datos fiscales de la razon social (por su `empresa_id`): lo que no viaja no se toca y la
   * historia no se sobrescribe. `version` es la del cliente del vinculo vigente (`null` sin el).
   */
  actualizarDatosFiscales(
    empresaId: number,
    datos: DatosFiscales,
    version: number | null,
    motivo: string,
  ): Observable<RespuestaDatosFiscales> {
    return this.api.pedir('PUT', `${this.base}/razones-sociales/${empresaId}`, {
      cuerpo: { razon_social: datos, ...(version === null ? {} : { version }), motivo },
    });
  }

  // ── Condiciones comerciales de cada pareja razon social → facturadora ──────

  /** Las parejas del cliente (con sus tramos de nomina) y la cadena de condiciones de cada una. */
  condicionesComerciales(clienteId: number): Observable<ListaCondicionesCliente> {
    return this.api.pedir('GET', `${this.base}/${clienteId}/condiciones-comerciales`);
  }

  /** La cadena completa de cada pareja que se toca; las demas no se tocan. `version` es la del cliente. */
  guardarCondicionesComerciales(
    clienteId: number,
    parejas: readonly DatosParejaCondiciones[],
    version: number,
    motivo: string,
  ): Observable<ListaCondicionesCliente> {
    return this.api.pedir('PUT', `${this.base}/${clienteId}/condiciones-comerciales`, {
      cuerpo: { parejas, version, motivo },
    });
  }

  // ── Broker y seguimiento: se guarda la cadena completa por vigencia ────────

  brokers(clienteId: number): Observable<ListaBrokersCliente> {
    return this.api.pedir('GET', `${this.base}/${clienteId}/brokers`);
  }

  guardarBrokers(
    clienteId: number,
    brokers: readonly DatosBroker[],
    version: number,
    motivo: string,
  ): Observable<ListaBrokersCliente> {
    return this.api.pedir('PUT', `${this.base}/${clienteId}/brokers`, { cuerpo: { brokers, version, motivo } });
  }

  seguimiento(clienteId: number): Observable<ListaSeguimientoCliente> {
    return this.api.pedir('GET', `${this.base}/${clienteId}/seguimiento`);
  }

  guardarSeguimiento(
    clienteId: number,
    seguimiento: readonly DatosSeguimiento[],
    version: number,
    motivo: string,
  ): Observable<ListaSeguimientoCliente> {
    return this.api.pedir('PUT', `${this.base}/${clienteId}/seguimiento`, {
      cuerpo: { seguimiento, version, motivo },
    });
  }

  // ── Esquemas de pago ───────────────────────────────────────────────────────

  esquemas(): Observable<EsquemaPago[]> {
    return this.api
      .pedir<{ esquemas: EsquemaPago[] }>('GET', `${this.base}/esquemas-pago`)
      .pipe(map((d) => d.esquemas));
  }

  altaEsquema(esquema: DatosEsquemaPago, motivo: string): Observable<EsquemaPago> {
    return this.api.pedir('POST', `${this.base}/esquemas-pago`, { cuerpo: { esquema, motivo } });
  }

  actualizarEsquema(id: number, esquema: DatosEsquemaPago, version: number, motivo: string): Observable<EsquemaPago> {
    return this.api.pedir('PUT', `${this.base}/esquemas-pago/${id}`, { cuerpo: { esquema, version, motivo } });
  }

  // ── Empresas pagadoras ─────────────────────────────────────────────────────

  empresas(): Observable<EmpresaPagadora[]> {
    return this.api
      .pedir<{ empresas: EmpresaPagadora[] }>('GET', `${this.base}/empresas-pagadoras`)
      .pipe(map((d) => d.empresas));
  }

  /** Habilita como pagadora una empresa ya dada de alta en catempresa (por RFC o id). */
  altaEmpresa(empresa: DatosEmpresaPagadora, motivo: string): Observable<EmpresaPagadora> {
    return this.api.pedir('POST', `${this.base}/empresas-pagadoras`, { cuerpo: { empresa, motivo } });
  }

  actualizarEmpresa(
    id: number,
    empresa: DatosEmpresaPagadora,
    version: number,
    motivo: string,
  ): Observable<EmpresaPagadora> {
    return this.api.pedir('PUT', `${this.base}/empresas-pagadoras/${id}`, { cuerpo: { empresa, version, motivo } });
  }

  // ── Relaciones comerciales (asignacion cliente en empresa, delgada) ────────

  relaciones(filtro: { cliente?: number; empresa?: number } = {}): Observable<ClienteEmpresa[]> {
    return this.api
      .pedir<{ asignaciones: DetalleRelacionComercial[] }>('GET', `${this.base}/relaciones-comerciales`, {
        consulta: filtro,
      })
      .pipe(map((d) => d.asignaciones.map(aClienteEmpresa)));
  }

  /** Alta: solo cliente, empresa y, opcionales, los esquemas sugeridos. Nunca la facturacion. */
  altaRelacion(asignacion: DatosRelacionComercial, motivo: string): Observable<ClienteEmpresa> {
    return this.api
      .pedir<DetalleRelacionComercial>('POST', `${this.base}/relaciones-comerciales`, {
        cuerpo: { asignacion, motivo },
      })
      .pipe(map(aClienteEmpresa));
  }

  /** Esquemas sugeridos y `activo` (con nominas activas no se da de baja: `NOMINAS_ACTIVAS`). */
  actualizarRelacion(
    id: number,
    asignacion: DatosRelacionComercial,
    version: number,
    motivo: string,
  ): Observable<ClienteEmpresa> {
    return this.api
      .pedir<DetalleRelacionComercial>('PUT', `${this.base}/relaciones-comerciales/${id}`, {
        cuerpo: { asignacion, version, motivo },
      })
      .pipe(map(aClienteEmpresa));
  }

  // ── Historial (bitacora comercial) ─────────────────────────────────────────

  historial(entidad: EntidadClientes, entidadId?: string | number, limite = 100): Observable<RegistroHistorial[]> {
    return this.api
      .pedir<{ registros: RegistroHistorial[] }>('GET', `${this.base}/historial`, {
        consulta: { entidad, entidad_id: entidadId, limite },
      })
      .pipe(map((d) => d.registros));
  }

  // ── Catalogos de apoyo ─────────────────────────────────────────────────────

  /** Claves SAT que factura una facturadora (`movempresacvesat`). */
  clavesSat(facturadoraId: number): Observable<ListaClavesSat> {
    return this.api.pedir('GET', `${this.base}/facturadoras/${facturadoraId}/claves-sat`);
  }

  /** Las empresas emisoras activas: las que pueden ser facturadoras (`facturadora_id`). */
  facturadoras(): Observable<Facturadora[]> {
    return this.api
      .pedir<{ facturadoras: Facturadora[] }>('GET', `${this.base}/catalogos/facturadoras`)
      .pipe(map((d) => d.facturadoras));
  }

  brokersCatalogo(): Observable<ElementoCatalogo[]> {
    return this.api
      .pedir<{ brokers: ElementoCatalogo[] }>('GET', `${this.base}/catalogos/brokers`)
      .pipe(map((d) => d.brokers));
  }

  /** Usuarios de SINAY para el seguimiento (ejecutivo / inplant). */
  usuariosCatalogo(): Observable<ElementoCatalogo[]> {
    return this.api
      .pedir<{ usuarios: ElementoCatalogo[] }>('GET', `${this.base}/catalogos/usuarios`)
      .pipe(map((d) => d.usuarios));
  }

  beneficiosCatalogo(): Observable<ElementoCatalogo[]> {
    return this.api
      .pedir<{ beneficios: ElementoCatalogo[] }>('GET', `${this.base}/catalogos/beneficios`)
      .pipe(map((d) => d.beneficios));
  }
}

/** `{asignacion, avisos}` → la asignacion que usan las pantallas (la relacion con sus avisos). */
export function aClienteEmpresa(d: DetalleRelacionComercial): ClienteEmpresa {
  return { ...d.asignacion, avisos: d.avisos ?? [] };
}
